import { CheckIcon, PencilSquareIcon, TrashIcon, VideoCameraIcon, MagnifyingGlassPlusIcon } from '@heroicons/react/24/outline';
import { IVideoClipResource, ResourceStatus } from '@packages/global';
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import MediaPreviewModal from '../../../../common/MediaPreviewModal';

interface ResourceItemProps {
  resource: IVideoClipResource;
  onApply: (videoUrl: string) => void;
  onReuse: (params: any) => void;
  onDelete: () => void;
}

const PromptDisplay = ({ prompt }: { prompt: string }) => {
  const { t } = useTranslation('editor');
  const [isExpanded, setIsExpanded] = useState(false);
  const [showToggle, setShowToggle] = useState(false);
  const textRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    if (textRef.current) {
      const { scrollHeight, clientHeight } = textRef.current;
      if (scrollHeight > clientHeight) {
        setShowToggle(true);
      }
    }
  }, [prompt]);

  return (
    <div className="bg-gray-800 rounded-lg p-3">
      <div className="flex items-center gap-2 text-gray-400 text-sm mb-2">
        <VideoCameraIcon className="h-4 w-4" />
        <span>{t('videoGenForm.videoPrompt')}</span>
      </div>
      <div className="relative">
        <p
          ref={textRef}
          className={`text-gray-200 text-sm leading-relaxed ${!isExpanded ? 'line-clamp-3' : ''}`}
        >
          {prompt}
        </p>
        {showToggle && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-blue-400 text-xs mt-1 hover:underline focus:outline-none"
          >
            {isExpanded ? t('videoGenForm.collapse') : t('videoGenForm.expand')}
          </button>
        )}
      </div>
    </div>
  );
};


export default function ResourceItem({ resource, onApply, onReuse, onDelete }: ResourceItemProps) {
  const { t } = useTranslation('editor');
  const [previewVideo, setPreviewVideo] = useState<string | null>(null);

  const statusConfig: Record<string, { label: string; color: string }> = {
    [ResourceStatus.PENDING]: { label: t('common.statusPending'), color: 'text-gray-400' },
    [ResourceStatus.GENERATING]: { label: t('common.statusGenerating'), color: 'text-blue-400' },
    [ResourceStatus.COMPLETED]: { label: t('common.statusCompleted'), color: 'text-green-400' },
    [ResourceStatus.FAILED]: { label: t('common.statusFailed'), color: 'text-red-400' }
  };

  let params = null;
  try {
    params = typeof resource.generationParams === 'string'
      ? JSON.parse(resource.generationParams)
      : resource.generationParams;
  } catch (e) {
    console.warn('Failed to parse generationParams:', e);
  }

  const statusInfo = statusConfig[resource.status];
  const isCompleted = resource.status === ResourceStatus.COMPLETED && resource.resultUrl;

  return (
    <div className="relative space-y-3 border-2 border-dashed border-gray-800 rounded-lg p-2 group">
      <button
        onClick={() => onReuse(params)}
        className="absolute top-2 right-2 p-1.5 text-gray-400 hover:text-white bg-gray-800/80 hover:bg-gray-700 rounded-lg backdrop-blur-sm transition-all opacity-0 group-hover:opacity-100 z-10"
        title={t('common.useParams')}
      >
        <PencilSquareIcon className="h-4 w-4" />
      </button>

      {params?.prompt && <PromptDisplay prompt={params.prompt} />}

      {isCompleted && (
        <div className="relative group/video">
          <video
            src={resource.resultUrl}
            controls
            className="w-full rounded-lg"
          />
          <button
            onClick={() => setPreviewVideo(resource.resultUrl!)}
            className="absolute top-2 left-2 p-1.5 text-gray-400 hover:text-white bg-gray-800/80 hover:bg-gray-700 rounded-lg backdrop-blur-sm transition-all opacity-0 group-hover/video:opacity-100 z-10"
            title={t('videoGenForm.enlargePreview')}
          >
            <MagnifyingGlassPlusIcon className="h-4 w-4" />
          </button>
        </div>
      )}

      {(resource.status === ResourceStatus.GENERATING || resource.status === ResourceStatus.PENDING) && (
        <div className="py-8">
          <div className="flex flex-col items-center gap-3 text-gray-400">
            <div className="h-8 w-8 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
            <span className="text-sm">{statusInfo.label}...</span>
          </div>
        </div>
      )}

      {resource.status === ResourceStatus.FAILED && (
        <div className="p-3 bg-red-900/30 rounded-lg text-red-400 text-sm">
          {resource.errorMessage || t('common.generationFailed')}
        </div>
      )}

      <div className="flex items-center justify-between">
        {isCompleted && (
          <button
            onClick={() => onApply(resource.resultUrl!)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 hover:bg-gray-700 text-gray-300 text-sm rounded-lg transition-colors"
          >
            <CheckIcon className="h-4 w-4" />
            <span>{t('videoGenForm.applyVideo')}</span>
          </button>
        )}
        <button
          onClick={onDelete}
          className="p-1.5 text-gray-400 hover:text-red-400 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
          title={t('common.deleteResource')}
        >
          <TrashIcon className="h-4 w-4" />
        </button>
      </div>

      <MediaPreviewModal
        isOpen={!!previewVideo}
        onClose={() => setPreviewVideo(null)}
        type="video"
        src={previewVideo || ''}
        alt={t('videoGenForm.videoPreview')}
      />
    </div>
  );
}
